import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getDoc, getDocs, doc, addDoc, deleteDoc, updateDoc, collection } from "firebase/firestore";
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import timelinePlugin from '@fullcalendar/timeline';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import moment from 'moment';


import { db } from "../../../firebase";
import { Button } from "../components/Buttons";

import { IoMdRefresh } from "react-icons/io";
import { FaCheck, FaRegCheckCircle, FaEnvelope } from "react-icons/fa";
import { RxLapTimer } from "react-icons/rx";
import fetch from 'node-fetch';

export const Meetings = () => {
    const { eventId } = useParams();
    const [wishes, setWishes] = useState({
        count: 0,
        wishes: 0,
        wishlist: []
    });
    const [event, setEvent] = useState({});
    const [meetings, setMeetings] = useState([]);
    const [dominantsCalendarResources, setDominantsCalendarResources] = useState(null);
    const [dominantsCalendarEvents, setDominantsCalendarEvents] = useState(null);
    const [dominatedCalendarResources, setDominatedCalendarResources] = useState(null);
    const [dominatedCalendarEvents, setDominatedCalendarEvents] = useState(null);
    const [meetingAmplitudes, setMeetingAmplitudes] = useState({});
    const [meetingDuration, setMeetingDuration] = useState(15);
    const [loadingMatchMaking, setLoadingMatchMaking] = useState(false);
    const [displayMode, setDisplayMode] = useState('dominants');
    const [eventUsers, setEventUsers] = useState({});
    const [wishUsers, setWishUsers] = useState({});
    const [displaySlotModal, setDisplaySlotModal] = useState(false);
    const [displayUnaffectedWishesModal, setDisplayUnaffectedWishesModal] = useState(false);
    const calendarRef = useRef(null);
    const [dataLoaded, setDataLoaded] = useState({
        events: false,
        eventUsers: false,
        wishes: false,
        meetings: false
    });

    const matchMaking = async () => {
        const r = window.confirm("Are you sure you want to generate meetings? This will delete all unvalidated meetings.");
        if(!r) return;
        setLoadingMatchMaking(true);
        const slots = event.slots;
        const slotsArray = Object.keys(slots).sort();
        
        // Compute statistics for participants to optimize matchmaking
        const dominantStats = {};
        const dominatedStats = {};
        wishes.wishlist.forEach(wish => {
            dominantStats[wish.dominant] = (dominantStats[wish.dominant] || 0) + 1;
            dominatedStats[wish.dominated] = (dominatedStats[wish.dominated] || 0) + 1;
        });

        // Sort wishlist to prioritize participants with fewer wishes first
        // This ensures everyone gets at least some meetings
        let sortedWishlist = [...wishes.wishlist].sort((a, b) => {
            // First prioritize by dominant with fewer wishes
            const dominantDiff = dominantStats[a.dominant] - dominantStats[b.dominant];
            if (dominantDiff !== 0) return dominantDiff;
            
            // Then by dominated with fewer wishes
            return dominatedStats[a.dominated] - dominatedStats[b.dominated];
        });

        // Get validated and blocked meetings to respect constraints
        const validatedMeetings = meetings.filter(m => m.status === 'validated' || m.status === 'bloqued');
        const blockedSlots = {};
        
        // Create map of blocked slots
        validatedMeetings.forEach(meeting => {
            if (!blockedSlots[meeting.slot]) blockedSlots[meeting.slot] = { dominants: {}, dominated: {} };
            blockedSlots[meeting.slot].dominants[meeting.dominant.id] = true;
            blockedSlots[meeting.slot].dominated[meeting.dominated.id] = true;
        });

        // Create a map for already scheduled meetings (validated ones)
        const scheduledPairs = {};
        validatedMeetings.forEach(meeting => {
            if (meeting.dominant.id && meeting.dominated.id) {
                const pairKey = `${meeting.dominant.id}_${meeting.dominated.id}`;
                scheduledPairs[pairKey] = true;
            }
        });

        console.log('Optimizing slot allocation...');
        let meetingItems = {};
        
        // Initialize slot availability
        slotsArray.forEach(slot => {
            meetingItems[slot] = [];
        });

        // First pass: assign meetings optimally
        for (const wish of sortedWishlist) {
            const pairKey = `${wish.dominant}_${wish.dominated}`;
            
            // Skip if this pair already has a validated meeting
            if (scheduledPairs[pairKey]) continue;
            
            // Find the best slot for this wish
            let bestSlot = null;
            let minConflicts = Infinity;
            
            for (const slot of slotsArray) {
                // Check if dominant or dominated is already booked in this slot
                const slotBlocked = blockedSlots[slot];
                if (slotBlocked && (slotBlocked.dominants[wish.dominant] || slotBlocked.dominated[wish.dominated])) {
                    continue;
                }
                
                // Count conflicts (how many other potential meetings would be blocked)
                const currentConflicts = countSlotConflicts(slot, wish, sortedWishlist, blockedSlots);
                
                if (currentConflicts < minConflicts) {
                    minConflicts = currentConflicts;
                    bestSlot = slot;
                }
            }
            
            // Assign the wish to the best slot found
            if (bestSlot) {
                meetingItems[bestSlot].push(wish);
                
                // Update blocked slots
                if (!blockedSlots[bestSlot]) blockedSlots[bestSlot] = { dominants: {}, dominated: {} };
                blockedSlots[bestSlot].dominants[wish.dominant] = true;
                blockedSlots[bestSlot].dominated[wish.dominated] = true;
            }
        }

        // Helper function to count how many other potential meetings would be blocked
        // if we assign this wish to this slot
        function countSlotConflicts(slot, wish, wishlist, blockedSlots) {
            let conflicts = 0;
            
            // Create a temporary blocked slot map
            const tempBlocked = { 
                dominants: {...(blockedSlots[slot]?.dominants || {})}, 
                dominated: {...(blockedSlots[slot]?.dominated || {})} 
            };
            
            // Add this wish's blocking
            tempBlocked.dominants[wish.dominant] = true;
            tempBlocked.dominated[wish.dominated] = true;
            
            // Count wishes that would be blocked
            wishlist.forEach(w => {
                if ((w.dominant === wish.dominant && w.dominated !== wish.dominated) || 
                    (w.dominant !== wish.dominant && w.dominated === wish.dominated)) {
                    conflicts++;
                }
            });
            
            return conflicts;
        }

        // Remove collection meetings under events/{eventId} with status pending
        const meetingsRef = collection(db, 'events', eventId, 'meetings');
        const meetingsSnapshot = await getDocs(meetingsRef);
        const deletePromises = [];
        meetingsSnapshot.forEach(doc => {
            if(doc.data().status === 'pending') {
                deletePromises.push(deleteDoc(doc.ref));
            }
        });
        
        await Promise.all(deletePromises);
        console.log('Cleared previous pending meetings');

        // Prepare user data for creating new meetings
        let dominants = {};
        let dominated = {};
        const addMeetingPromises = [];

        // Create meetings for each assigned wish
        for(const slot of Object.keys(meetingItems)) {
            for(const meeting of meetingItems[slot]) {
                if(!dominants[meeting.dominant]) {
                    const dominantRef = await getDoc(doc(db, 'users', meeting.dominant));
                    let dominant = dominantRef.data();
                    dominant.id = dominantRef.id;
                    const dominantCompany = await getDoc(dominant.company);
                    dominants[meeting.dominant] = {
                        id: dominant.id,
                        path: dominantRef.ref.path,
                        firstname: dominant.firstname,
                        lastname: dominant.lastname,
                        position: dominant.role ? dominant.role : '',
                        company: dominantCompany.data().name,
                    };
                }

                if(!dominated[meeting.dominated]) {
                    const dominatedRef = await getDoc(doc(db, 'users', meeting.dominated));
                    let dominatedUser = dominatedRef.data();
                    dominatedUser.id = dominatedRef.id;
                    const dominatedCompany = await getDoc(dominatedUser.company);
                    dominated[meeting.dominated] = {
                        id: dominatedUser.id,
                        path: dominatedRef.ref.path,
                        firstname: dominatedUser.firstname,
                        lastname: dominatedUser.lastname,
                        position: dominatedUser.role ? dominatedUser.role : '',
                        company: dominatedCompany.data().name
                    };
                }

                // Create document for meeting with dominant and dominated and slot
                addMeetingPromises.push(
                    addDoc(collection(db, 'events', eventId, 'meetings'), {
                        dominant: dominants[meeting.dominant],
                        dominated: dominated[meeting.dominated],
                        slot: slot,
                        status: 'pending',
                        date: new Date()
                    })
                );
            }
        }

        await Promise.all(addMeetingPromises);
        console.log(`Created ${addMeetingPromises.length} new meetings`);
        
        getMeetings();
        setLoadingMatchMaking(false);
    }

    const getEvents = async () => {
        // List all documents in events/{eventId}/wishes/*
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        let eventData = eventDoc.data();
        eventData.id = eventDoc.id;

        let tmpMeetingAmplitudes = {};
        let tmpMeetingDuration = 120;
        let tmpSlotTimes = [];

        Object.keys(eventData.slots).forEach((slot) => {
            const beginTime = eventData.slots[slot].begin.toDate();
            const endTime = eventData.slots[slot].end.toDate();

            // Collect all unique times (begin and end of each slot)
            tmpSlotTimes.push(beginTime);
            tmpSlotTimes.push(endTime);

            if(!tmpMeetingAmplitudes.min || tmpMeetingAmplitudes.min > beginTime) {
                tmpMeetingAmplitudes.min = beginTime;
            }
            if(!tmpMeetingAmplitudes.max || tmpMeetingAmplitudes.max < endTime) {
                tmpMeetingAmplitudes.max = endTime;
            }

            // Calculate duration in minutes, ignoring timezone changes (DST)
            // Use UTC to avoid DST issues
            const slotDuration = Math.round((endTime - beginTime) / (1000 * 60));
            console.log(`Slot ${slot}: ${moment(beginTime).format('HH:mm')} - ${moment(endTime).format('HH:mm')} = ${slotDuration} minutes`);
            if(slotDuration < tmpMeetingDuration) {
                tmpMeetingDuration = slotDuration;
            }
        });

        // Sort and remove duplicates
        tmpSlotTimes = [...new Set(tmpSlotTimes.map(t => t.getTime()))].sort().map(t => new Date(t));

        const duration = moment.duration(tmpMeetingDuration, 'minutes');
        // setMeetingDuration in format HH:mm:ss
        const formattedDuration = `${duration.hours().toString().padStart(2, '0')}:${duration.minutes().toString().padStart(2, '0')}:00`;
        console.log('=== CALENDAR CONFIGURATION ===');
        console.log('Smallest slot duration (minutes):', tmpMeetingDuration);
        console.log('Formatted duration for FullCalendar:', formattedDuration);
        console.log('Meeting amplitudes:', tmpMeetingAmplitudes);
        console.log('Slot times:', tmpSlotTimes);
        setMeetingDuration(formattedDuration);
        setMeetingAmplitudes(tmpMeetingAmplitudes);

        setEvent(eventData);
        setDataLoaded(prev => ({ ...prev, events: true }));
    }

    const getWishes = async () => {
        // Get all wishlist documents in one batch
        const wishesRef = collection(db, 'events', eventId, 'wishlist');
        const wishesSnapshot = await getDocs(wishesRef);
        
        let wishesCount = 0;
        let dominantsCount = 0;
        let tmpWishlist = [];
        let tmpWishUsers = {};
        
        // Track user IDs that need fetching to avoid duplicate requests
        const userIdsToFetch = new Set();
        const companyIdsToFetch = new Set();
        
        // First, collect all user IDs that need to be fetched
        wishesSnapshot.docs.forEach(dominant => {
            const data = dominant.data();
            if (data.users && data.users.length > 0) {
                userIdsToFetch.add(dominant.id);
                data.users.forEach(user => userIdsToFetch.add(user.id));
            }
        });
        
        // Batch fetch all users data
        const userDocs = await Promise.all(
            Array.from(userIdsToFetch).map(userId => 
                getDoc(doc(db, 'users', userId))
            )
        );
        
        // Create a map of user data
        const usersMap = {};
        userDocs.forEach(userDoc => {
            if (userDoc.exists()) {
                const userData = userDoc.data();
                userData.id = userDoc.id;
                usersMap[userDoc.id] = userData;
                
                // Collect company IDs to fetch
                if (userData.company) {
                    companyIdsToFetch.add(userData.company.path || userData.company.id);
                }
            }
        });
        
        // Batch fetch all companies data
        const companyDocs = await Promise.all(
            Array.from(companyIdsToFetch).map(companyPath => {
                // Handle both reference paths and direct IDs
                if (typeof companyPath === 'string' && companyPath.includes('/')) {
                    // It's a path
                    return getDoc(doc(db, companyPath));
                } else {
                    // It's an ID
                    return getDoc(doc(db, 'companies', companyPath));
                }
            })
        );
        
        // Create a map of company data
        const companiesMap = {};
        companyDocs.forEach(companyDoc => {
            if (companyDoc.exists()) {
                companiesMap[companyDoc.id] = companyDoc.data();
            }
        });
        
        // Process the wishlist with our cached data
        wishesSnapshot.docs.forEach(dominant => {
            const data = dominant.data();
            if (data.users && data.users.length > 0) {
                dominantsCount++;
                wishesCount += data.users.length;
                
                const dominantData = usersMap[dominant.id];
                if (dominantData) {
                    const companyId = dominantData.company?.id || (dominantData.company?.path || '').split('/').pop();
                    const companyName = companiesMap[companyId]?.name || 'Unknown Company';
                    
                    const enrichedDominantData = {
                        ...dominantData,
                        company: companyName,
                        type: 'dominant',
                        date: eventUsers[dominantData.email]?.date
                    };
                    
                    tmpWishUsers[dominant.id] = enrichedDominantData;
                    
                    data.users.forEach(user => {
                        const dominatedData = usersMap[user.id];
                        if (dominatedData) {
                            const dominatedCompanyId = dominatedData.company?.id || (dominatedData.company?.path || '').split('/').pop();
                            const dominatedCompanyName = companiesMap[dominatedCompanyId]?.name || 'Unknown Company';
                            
                            tmpWishUsers[user.id] = {
                                ...dominatedData,
                                company: dominatedCompanyName,
                                type: 'dominated',
                                date: eventUsers[dominatedData.email]?.date
                            };
                            
                            tmpWishlist.push({
                                dominant: dominant.id,
                                dominated: user.id
                            });
                        }
                    });
                }
            }
        });
        
        setWishUsers(tmpWishUsers);
        console.log({
            count: dominantsCount,
            wishes: wishesCount,
            wishlist: tmpWishlist
        });
        setWishes({
            count: dominantsCount,
            wishes: wishesCount,
            wishlist: tmpWishlist
        });
        setDataLoaded(prev => ({ ...prev, wishes: true }));
    };

    const getMeetings = async () => {
        const meetingsRef = collection(db, 'events', eventId, 'meetings');
        const meetingsSnapshot = await getDocs(meetingsRef);
        let tmpMeetings = [];
        meetingsSnapshot.forEach((doc) => {
            let meeting = doc.data();
            console.log("meetingData:", meeting);
            meeting.id = doc.id;
            tmpMeetings.push(meeting);
        });
        setMeetings(tmpMeetings);
        setDataLoaded(prev => ({ ...prev, meetings: true }));
    }

    /**
     * Envoi des meetings validés par email aux utilisateurs
     * @param {array} meetingIds 
     */
    const sendMeetings = async (meetingIds) => {
        try {
            const response = await fetch(`${window.fdVariables.apiBaseurl}/meetings/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    event: event.id,
                    meetings: meetingIds
                })
            });
            const responseData = await response.json();
            if(responseData.success) {
                alert('Meetings sent successfully');
            } else {
                alert('An error occurred while sending the meetings. Please try again later.');
            }
        } catch (error) {
            console.error('Error sending meetings:', error);
            alert('An error occurred while sending the meetings. Please try again later.');
        }
    }

    const getEventUsers = async () => {
        const eventUsersRef = collection(db, 'events', eventId, 'users');
        const eventUsersSnapshot = await getDocs(eventUsersRef);
        let tmpEventUsers = {};
        eventUsersSnapshot.forEach((doc) => {
            let eventUser = doc.data();
            eventUser.id = doc.id;
            tmpEventUsers[eventUser.id] = eventUser;
        });
        setEventUsers(tmpEventUsers);
        setDataLoaded(prev => ({ ...prev, eventUsers: true }));
    }

    useEffect(() => {
        const loadInitialData = async () => {
            await getEvents();
            await getEventUsers();
            // Attendons que les données des utilisateurs soient chargées avant de charger les souhaits
            await getWishes();
            await getMeetings();
        };
        
        loadInitialData();
        // eslint-disable-next-line
    }, []);

    // Nouvel useEffect pour attendre que toutes les données soient prêtes
    useEffect(() => {
        const allDataLoaded = Object.values(dataLoaded).every(value => value === true);
        if (allDataLoaded) {
            console.log('Toutes les données sont chargées, mise à jour du calendrier');
            updateCalendarData();
        }
        // eslint-disable-next-line
    }, [dataLoaded]);

    // Fonction séparée pour mettre à jour les données du calendrier
    const updateCalendarData = () => {
        if(meetings && Array.isArray(meetings) && event && event.slots && wishUsers && Object.keys(wishUsers).length > 0) {
            let dominantsResources = [];
            let dominantsEvents = [];
            let dominatedResources = [];
            let dominatedEvents = [];

            Object.keys(wishUsers).forEach(eventUserId => {
                let eventUser = wishUsers[eventUserId];
                if(eventUser.type === 'dominant') {
                    dominantsResources.push({
                        id: eventUser.id,
                        title: eventUser.company,
                        date: eventUser.date,
                        extendedProps: {
                            dominantId: eventUser.id,
                            firstname: eventUser.firstname,
                            lastname: eventUser.lastname,
                            company: eventUser.company,
                            date: eventUser.date,
                            unaffectedMeetings: []
                        }
                    });
                } else if (eventUser.type === 'dominated') {
                    dominatedResources.push({
                        id: eventUser.id,
                        title: eventUser.company,
                        date: eventUser.date,
                        extendedProps: {
                            dominantId: eventUser.id,
                            firstname: eventUser.firstname,
                            lastname: eventUser.lastname,
                            company: eventUser.company,
                            date: eventUser.date,
                            unaffectedMeetings: []
                        }
                    });
                }
            });

            meetings.forEach(meeting => {
                // Add the meeting to the events
                if (meeting.dominant && meeting.dominant.id) {
                    dominantsEvents.push({
                        resourceId: meeting.dominant.id,
                        title: meeting.dominated.company,
                        extendedProps: {
                            meetingId: meeting.id,
                            dominantId: meeting.dominant.id,
                            dominatedId: meeting.dominated.id,
                            slot: meeting.slot,
                            firstname: meeting.dominated.firstname,
                            lastname: meeting.dominated.lastname,
                            company: meeting.dominated.company,
                            status: meeting.status,
                            validationStatus: meeting.dominated.status === 'validated' ? 'validated' : 'pending'
                        },
                        start: event.slots[meeting.slot].begin.toDate(),
                        end: event.slots[meeting.slot].end.toDate()
                    });
                }

                if (meeting.dominated && meeting.dominated.id) {
                    dominatedEvents.push({
                        resourceId: meeting.dominated.id,
                        title: meeting.dominant.company,
                        extendedProps: {
                            meetingId: meeting.id,
                            dominantId: meeting.dominant.id,
                            dominatedId: meeting.dominated.id,
                            slot: meeting.slot,
                            firstname: meeting.dominant.firstname,
                            lastname: meeting.dominant.lastname,
                            company: meeting.dominant.company,
                            status: meeting.status,
                            validationStatus: meeting.dominated.status === 'validated' ? 'validated' : 'pending'
                        },
                        start: event.slots[meeting.slot].begin.toDate(),
                        end: event.slots[meeting.slot].end.toDate()
                    });
                }
            });

            // If wish is not in the meetings, add it to the unaffectedMeetings extendedProps
            dominantsResources.forEach((resource, i) => {
                wishes.wishlist.forEach((wish) => {
                    if(wish.dominant === resource.id && !meetings.find((meeting) => 
                        meeting.dominant && meeting.dominated && 
                        meeting.dominant.id === wish.dominant && 
                        meeting.dominated.id === wish.dominated)) {
                        dominantsResources[i].extendedProps.unaffectedMeetings.push(wish.dominated);
                    }
                });
            });

            dominatedResources.forEach((resource, i) => {
                wishes.wishlist.forEach((wish) => {
                    if(wish.dominated === resource.id && !meetings.find((meeting) => 
                        meeting.dominant && meeting.dominated && 
                        meeting.dominant.id === wish.dominant && 
                        meeting.dominated.id === wish.dominated)) {
                        dominatedResources[i].extendedProps.unaffectedMeetings.push(wish.dominant);
                    }
                });
            });
            
            setDominantsCalendarResources(dominantsResources);
            setDominantsCalendarEvents(dominantsEvents);
            setDominatedCalendarResources(dominatedResources);
            setDominatedCalendarEvents(dominatedEvents);
        }
    };

    const handleMeetingDrop = async (arg) => {
        let newDoc = {};
        for(const slot of Object.keys(event.slots)) {
            if(moment(arg.event.start).format('YYYY-MM-DD HH:mm:ss') === moment(event.slots[slot].begin.toDate()).format('YYYY-MM-DD HH:mm:ss')) {
                newDoc.slot = slot;
            }
        }

        if(arg.newResource && arg.oldResource.id !== arg.newResource.id) {
            // Mettre à jour le dominant avec statut pending
            newDoc.dominant = {
                id: arg.newResource.extendedProps.dominantId,
                path: doc(db, 'users', arg.newResource.extendedProps.dominantId).path,
                company: arg.newResource.extendedProps.company,
                firstname: arg.newResource.extendedProps.firstname,
                lastname: arg.newResource.extendedProps.lastname,
                status: 'pending',
            };
            
            // Récupérer les informations sur le dominated pour aussi mettre son statut à pending
            const meetingDoc = await getDoc(doc(db, 'events', eventId, 'meetings', arg.event.extendedProps.meetingId));
            const meetingData = meetingDoc.data();
            if (meetingData && meetingData.dominated) {
                newDoc.dominated = {
                    ...meetingData.dominated,
                    status: 'pending'
                };
            }
        } else {
            // Si seul le créneau change, mettre à jour les statuts des deux participants
            const meetingDoc = await getDoc(doc(db, 'events', eventId, 'meetings', arg.event.extendedProps.meetingId));
            const meetingData = meetingDoc.data();
            if (meetingData) {
                if (meetingData.dominant) {
                    newDoc.dominant = {
                        ...meetingData.dominant,
                        status: 'pending'
                    };
                }
                if (meetingData.dominated) {
                    newDoc.dominated = {
                        ...meetingData.dominated,
                        status: 'pending'
                    };
                }
            }
        }

        // Ajouter aussi le statut global de la réunion
        newDoc.status = 'pending';

        // Check if dominant or dominated is available in the slot
        let block = false;
        meetings.map((meeting) => {
            if(block === false && meeting.slot === newDoc.slot && (arg.event.extendedProps.dominantId === meeting.dominant.id || arg.event.extendedProps.dominatedId === meeting.dominated.id)) {
                block = true;
                alert('This slot is already taken by dominant or dominated');
            }
            return true;
        });

        if(block === false) {
            if(newDoc.slot || newDoc.dominant) {
                // Update the slot of the meeting
                const meetingRef = doc(db, 'events', eventId, 'meetings', arg.event.extendedProps.meetingId);
                await updateDoc(meetingRef, newDoc);
            }
        }
        getMeetings();
    }

    const deleteMeeting = async (meetingId) => {
        if(window.confirm("Are you sure you want to delete this meeting?") === false) return;

        const meetingRef = doc(db, 'events', eventId, 'meetings', meetingId);
        await deleteDoc(meetingRef);
        getMeetings();
    }
    
    const validateMeetings = async () => {
        if(window.confirm("Are you sure you want to validate all meetings? Email will be sent to users.") === false) return;

        const meetingsRef = collection(db, 'events', eventId, 'meetings');
        const meetingsSnapshot = await getDocs(meetingsRef);
        let meetingsToSend = [];
        for(const doc of meetingsSnapshot.docs) {
            if(doc.data().status === 'pending') {
                await updateDoc(doc.ref, { status: 'validated' });
                meetingsToSend.push(doc.id);
            }
        };
        if(meetingsToSend.length > 0) sendMeetings(meetingsToSend);
        getMeetings();
    }

    const sendNoMeetingsEmail = async () => {
        if(window.confirm("Êtes-vous sûr de vouloir envoyer les emails aux participants sans rendez-vous ?") === false) return;

        try {
            const response = await fetch(`${window.fdVariables.apiBaseurl}/email/no-meetings/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    event: event.id
                })
            });
            const responseData = await response.json();
            if(responseData.success) {
                alert('Emails envoyés avec succès');
            } else {
                alert('Une erreur est survenue lors de l\'envoi des emails. Veuillez réessayer plus tard.');
            }
        } catch (error) {
            console.error('Error sending no-meetings emails:', error);
            alert('Une erreur est survenue lors de l\'envoi des emails. Veuillez réessayer plus tard.');
        }
    }

    useEffect(() => {
        if(meetings && Array.isArray(meetings) && event && event.slots && wishUsers && Object.keys(wishUsers).length > 0) {
            let dominantsResources = [];
            let dominantsEvents = [];
            let dominatedResources = [];
            let dominatedEvents = [];

            Object.keys(wishUsers).forEach(eventUserId => {
                let eventUser = wishUsers[eventUserId];
                if(eventUser.type === 'dominant') {
                    dominantsResources.push({
                        id: eventUser.id,
                        title: eventUser.company,
                        date: eventUser.date,
                        extendedProps: {
                            dominantId: eventUser.id,
                            firstname: eventUser.firstname,
                            lastname: eventUser.lastname,
                            company: eventUser.company,
                            date: eventUser.date,
                            unaffectedMeetings: []
                        }
                    });
                } else if (eventUser.type === 'dominated') {
                    dominatedResources.push({
                        id: eventUser.id,
                        title: eventUser.company,
                        date: eventUser.date,
                        extendedProps: {
                            dominantId: eventUser.id,
                            firstname: eventUser.firstname,
                            lastname: eventUser.lastname,
                            company: eventUser.company,
                            date: eventUser.date,
                            unaffectedMeetings: []
                        }
                    });
                }
            });

            meetings.map(meeting => {
                // Add the meeting to the events
                dominantsEvents.push({
                    resourceId: meeting.dominant.id,
                    title: meeting.dominated.company,
                    extendedProps: {
                        meetingId: meeting.id,
                        dominantId: meeting.dominant.id,
                        dominatedId: meeting.dominated.id,
                        slot: meeting.slot,
                        firstname: meeting.dominated.firstname,
                        lastname: meeting.dominated.lastname,
                        company: meeting.dominated.company,
                        status: meeting.status,
                        validationStatus: meeting.dominated.status === 'validated' ? 'validated' : 'pending'
                    },
                    start: event.slots[meeting.slot].begin.toDate(),
                    end: event.slots[meeting.slot].end.toDate()
                });

                dominatedEvents.push({
                    resourceId: meeting.dominated.id,
                    title: meeting.dominant.company,
                    extendedProps: {
                        meetingId: meeting.id,
                        dominantId: meeting.dominant.id,
                        dominatedId: meeting.dominated.id,
                        slot: meeting.slot,
                        firstname: meeting.dominant.firstname,
                        lastname: meeting.dominant.lastname,
                        company: meeting.dominant.company,
                        status: meeting.status,
                        validationStatus: meeting.dominated.status === 'validated' ? 'validated' : 'pending'
                    },
                    start: event.slots[meeting.slot].begin.toDate(),
                    end: event.slots[meeting.slot].end.toDate()
                });

                return true;
            });

            // If wish is not in the meetings, add it to the unaffectedMeetings extendedProps
            dominantsResources.map((resource, i) => {
                wishes.wishlist.map((wish) => {
                    if(wish.dominant === resource.id && !meetings.find((meeting) => meeting.dominant.id === wish.dominant && meeting.dominated.id === wish.dominated)) {
                        dominantsResources[i].extendedProps.unaffectedMeetings.push(wish.dominated);
                    }
                    return true;
                });
                return true;
            });

            dominatedResources.map((resource, i) => {
                wishes.wishlist.map((wish) => {
                    if(wish.dominated === resource.id && !meetings.find((meeting) => meeting.dominant.id === wish.dominant && meeting.dominated.id === wish.dominated)) {
                        dominatedResources[i].extendedProps.unaffectedMeetings.push(wish.dominant);
                    }
                    return true;
                });
                return true;
            });
            
            setDominantsCalendarResources(dominantsResources);
            setDominantsCalendarEvents(dominantsEvents);
            setDominatedCalendarResources(dominatedResources);
            setDominatedCalendarEvents(dominatedEvents);
        }
    }, [meetings, event, wishUsers, wishes]);

    const blockSlot = async (arg) => {
        const slot = Object.keys(event.slots).find((slot) => moment(event.slots[slot].begin.toDate()).format('YYYY-MM-DD HH:mm:ss') === moment(arg.date).format('YYYY-MM-DD HH:mm:ss'));
        let doc = {};
        if(displayMode === 'dominants') {
            doc = {
                dominant: {
                    id: arg.resource.extendedProps.dominantId,
                    company: arg.resource.extendedProps.company,
                    firstname: arg.resource.extendedProps.firstname,
                    lastname: arg.resource.extendedProps.lastname,
                    path: `users/${arg.resource.extendedProps.dominantId}`
                },
                dominated: {
                    id: null,
                    company: 'Blocked slot',
                    firstname: 'blocked',
                    lastname: 'slot',
                },
                slot: slot,
                status: 'bloqued',
                date: new Date()
            };
        } else {
            doc = {
                dominant: {
                    id: null,
                    company: 'Blocked slot',
                    firstname: 'blocked',
                    lastname: 'slot',
                },
                dominated: {
                    id: arg.resource.extendedProps.dominatedId,
                    company: arg.resource.extendedProps.company,
                    firstname: arg.resource.extendedProps.firstname,
                    lastname: arg.resource.extendedProps.lastname,
                },
                slot: slot,
                status: 'bloqued',
                date: new Date()
            };
        }
        
        addDoc(collection(db, 'events', eventId, 'meetings'), doc);
        getMeetings();
    }

    const addMeeting = async (arg, resource, attendee) => {
        const slot = Object.keys(event.slots).find((slot) => moment(event.slots[slot].begin.toDate()).format('YYYY-MM-DD HH:mm:ss') === moment(arg.date).format('YYYY-MM-DD HH:mm:ss'));
        let doc = {};
        if(displayMode === 'dominants') {
            doc = {
                dominant: {
                    id: arg.resource.extendedProps.dominantId,
                    company: arg.resource.extendedProps.company,
                    firstname: arg.resource.extendedProps.firstname,
                    lastname: arg.resource.extendedProps.lastname,
                    path: `users/${arg.resource.extendedProps.dominantId}`
                },
                dominated: {
                    id: attendee.id,
                    company: attendee.company,
                    firstname: attendee.firstname,
                    lastname: attendee.lastname,
                    path: `users/${attendee.id}`
                },
                slot: slot,
                status: 'pending',
                date: new Date()
            };
        } else {
            doc = {
                dominant: {
                    id: attendee.id,
                    company: attendee.company,
                    firstname: attendee.firstname,
                    lastname: attendee.lastname,
                    path: `users/${attendee.id}`
                },
                dominated: {
                    id: resource.dominantId,
                    company: resource.company,
                    firstname: resource.firstname,
                    lastname: resource.lastname,
                    path: `users/${resource.dominantId}`
                },
                slot: slot,
                status: 'pending',
                date: new Date()
            };
        }

        addDoc(collection(db, 'events', eventId, 'meetings'), doc);
        getMeetings();
    }

    const UnaffectedWishesModal = ({display}) => {
        if(!display) return null;

        return (
            <div className="fixed z-50 inset-0 flex items-center justify-center overflow-hidden">
                <div className="fixed inset-0 transition-opacity" onClick={() => setDisplayUnaffectedWishesModal(false)}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <div className="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full z-50">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                            Souhaits non planifiés - {display.resource.title}
                        </h3>
                        <div className="text-sm text-gray-600 mb-2">
                            {display.resource.extendedProps.firstname} {display.resource.extendedProps.lastname}
                        </div>
                        <div className="mt-4 max-h-96 overflow-y-auto">
                            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                                {display.unaffectedMeetings && display.unaffectedMeetings.length > 0 && display.unaffectedMeetings.map((meetingId, i) => {
                                    const wish = wishUsers[meetingId];
                                    if (!wish) return null;
                                    return (
                                        <li className="p-3 hover:bg-gray-50" key={i}>
                                            <div className="font-semibold text-gray-900">{wish.company}</div>
                                            <div className="text-sm text-gray-600">{wish.firstname} {wish.lastname}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
                            onClick={() => setDisplayUnaffectedWishesModal(false)}
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const SlotModal = ({display}) => {

        const handleBlockSlot = () => {
            if(window.confirm(`Are you sure you want to block this slot for ${display.resource.title} ?`) === false) return;
            blockSlot(display);
            setDisplaySlotModal(false);
        }

        const handleAddSlot = (resource, attendee) => {
            if(window.confirm(`Are you sure you want to add a meeting for ${display.resource.title} ?`) === false) return;
            addMeeting(display, resource, attendee);
            setDisplaySlotModal(false);
        }

        if(display) return (
            <div className="fixed z-50 inset-0 flex items-center justify-center overflow-hidden">
                <div className="fixed inset-0 transition-opacity">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <div className="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Meeting adjustment
                        </h3>
                        <div className="mt-2">
                            <ul className="divide-y divide-gray-200 shadow-md">
                                <li className="flex justify-between items-center user-card border-b border-grey-200">
                                    <button className="w-full text-left p-3" onClick={handleBlockSlot}>Block Slot</button>
                                </li>
                                {display.resource.extendedProps.unaffectedMeetings && display.resource.extendedProps.unaffectedMeetings.length > 0 && display.resource.extendedProps.unaffectedMeetings.map((meeting, i) => (
                                    <li className="flex justify-between items-center user-card border-b border-grey-200" key={i}>
                                        <button className="w-full text-left p-3" onClick={() => handleAddSlot(display.resource.extendedProps, wishUsers[meeting])}>{wishUsers[meeting].company}</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Accept
                        </button>
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-grey-500 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={() => setDisplaySlotModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container mx-auto">
            <div className="flex justify-between">
                <div>
                    <h1 className="text-5xl">{event.name} : Meetings</h1>
                    <p>{wishes.count} dominants made {wishes.wishes} whishes</p>
                    {meetings.length > 0 && <p>{meetings.length} meetings have been generated</p>}
                </div>
                <div className="flex gap-2">
                    <div>
                        <Button onClick={matchMaking} className="flex items-center font-bold bg-red-400"><IoMdRefresh className="mr-2" /> {loadingMatchMaking ? "Generating..." : "MatchMaking"}</Button>
                    </div>
                    {meetings && Array.isArray(meetings) && meetings.find((meeting) => meeting.status === 'validated') && (
                        <div>
                            <Button onClick={sendNoMeetingsEmail} className="flex items-center font-bold bg-orange-400 text-truncate">
                                Sans rendez-vous <FaEnvelope className="ml-2" />
                            </Button>
                        </div>
                    )}
                    <div>
                        {meetings && Array.isArray(meetings) && meetings.find((meeting) => meeting.status === 'pending') && <Button onClick={validateMeetings} className="flex items-center font-bold bg-green-400"><FaCheck className="mr-2" />Validate Meetings</Button>}
                    </div>
                </div>
            </div>
            <div className="flex gap-2 mt-4">
                <Button className={displayMode === 'dominants' ? 'bg-blue' : 'bg-grey-400'} onClick={() => setDisplayMode('dominants')}>Dominants</Button>
                <Button className={displayMode === 'dominated' ? 'bg-blue' : 'bg-grey-400'} onClick={() => setDisplayMode('dominated')}>Dominated</Button>
            </div>

            <div className="flex w-full mt-4">
                    {event && event.startDate && dominantsCalendarResources && dominatedCalendarResources && (
                        <>
                            <UnaffectedWishesModal display={displayUnaffectedWishesModal} />
                            <SlotModal display={displaySlotModal} />
                            <FullCalendar
                                height="auto"
                                ref={calendarRef}
                                plugins={[interactionPlugin, timelinePlugin, resourceTimelinePlugin]}
                                initialView="resourceTimelineDay"
                                initialDate={moment(event.startDate.toDate()).toDate()}
                                scrollTime={moment(event.startDate.toDate()).format('HH:mm:ss')}
                                resources={displayMode === 'dominants' ? dominantsCalendarResources : dominatedCalendarResources}
                                resourceOrder="-date"
                                events={displayMode === 'dominants' ? dominantsCalendarEvents : dominatedCalendarEvents}
                                slotMinWidth={120}
                                slotDuration={meetingDuration}
                                slotLabelInterval={meetingDuration}
                                snapDuration={meetingDuration}
                                slotMinTime={moment(meetingAmplitudes.min).format('HH:mm:ss')}
                                slotMaxTime={moment(meetingAmplitudes.max).format('HH:mm:ss')}
                                locale='fr-FR'
                                editable={true}
                                eventDrop={handleMeetingDrop}
                                dateClick={setDisplaySlotModal}
                                resourceLabelContent = {(arg) => {
                                    return (
                                        <div className="flex flex-col">
                                            <div><b>{arg.resource.title}</b></div>
                                            <div className="text-xs truncate" style={{ fontSize: '0.8rem' }}><i>{arg.resource.extendedProps.firstname} {arg.resource.extendedProps.lastname}</i></div>
                                            <div>
                                                {arg.resource.extendedProps.unaffectedMeetings && arg.resource.extendedProps.unaffectedMeetings.length > 0 && (
                                                    <button
                                                        className="text-xs bg-red-400 text-white px-2 py-1 rounded mt-1 hover:bg-red-500 transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDisplayUnaffectedWishesModal({
                                                                resource: arg.resource,
                                                                unaffectedMeetings: arg.resource.extendedProps.unaffectedMeetings
                                                            });
                                                        }}
                                                    >
                                                        {arg.resource.extendedProps.unaffectedMeetings.length} souhait{arg.resource.extendedProps.unaffectedMeetings.length > 1 ? 's' : ''}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }}
                                eventContent={(arg) => {
                                    console.log(arg.event.extendedProps);
                                    return (
                                        <div className={`p-1 m-1 ${arg.event.extendedProps.status === 'validated' ? arg.event.extendedProps.validationStatus === 'validated' ? 'bg-green-900' : 'bg-green-400' : arg.event.extendedProps.status === 'bloqued' ? 'bg-red-400' : 'bg-orange'} rounded-md`} onClick={() => deleteMeeting(arg.event.extendedProps.meetingId)}>
                                            {arg.event.extendedProps.status === 'validated' && <div style={{ position: 'absolute', top: 10, right: 10}}>{arg.event.extendedProps.validationStatus === 'validated' ? <FaRegCheckCircle className="text-white" /> : <RxLapTimer className="text-white" />}</div>}
                                            <b>{arg.event.title}</b>
                                            <div className="text-xs truncate" style={{ fontSize: '0.8rem' }}><i>{arg.event.extendedProps.firstname} {arg.event.extendedProps.lastname}</i></div>
                                        </div>
                                    );
                                }}
                            />
                            <style>
                                {`
                                    .fc-license-message {
                                        display: none;
                                    }
                                    .fc-event {
                                        background-color: transparent;
                                        border: none;
                                        border-radius: 10px;
                                    }
                                `}
                            </style>
                        </>
                    )}
                </div>
        </div>
    );
};